import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { CalendarDays } from 'lucide-react';
import { api } from '@/lib/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { formatDate } from '@/lib/utils';

// TODO(psur): true month/grid calendar UI deferred. MVP shows grouped list by urgency.

interface PsurCalendarItem {
  id: string;
  product: string | null;
  reportNumber: string | null;
  interval: string | null;
  dlp: string | null;
  dueAt: string;
  status: string | null;
}

export function PsurCalendarPage() {
  const cal = useQuery({
    queryKey: ['psur-calendar'],
    queryFn: async () => {
      try {
        return (await api.get('/api/psur-draft/calendar')).data;
      } catch {
        return { items: [] };
      }
    },
  });

  const items: PsurCalendarItem[] = cal.data?.items ?? [];
  const now = Date.now();
  const DAY = 1000 * 60 * 60 * 24;

  const overdue = items.filter((i) => new Date(i.dueAt).getTime() < now);
  const within30 = items.filter((i) => {
    const d = new Date(i.dueAt).getTime();
    return d >= now && d - now < 30 * DAY;
  });
  const later = items.filter((i) => new Date(i.dueAt).getTime() - now >= 30 * DAY);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">PSUR calendar</h1>
          <p className="text-sm text-muted-foreground">
            Upcoming PSUR due dates highlighted by proximity. Red: overdue, amber: under 30 days,
            green: 30+ days.
          </p>
        </div>
        <Button asChild variant="outline">
          <Link to="/psur">All PSURs</Link>
        </Button>
      </div>

      <div className="grid gap-4 md:grid-cols-3">
        <StatCard label="Overdue" value={overdue.length} tone="destructive" />
        <StatCard label="Due < 30d" value={within30.length} tone="warning" />
        <StatCard label="Due 30d+" value={later.length} tone="success" />
      </div>

      <Section title="Overdue" items={overdue} tone="destructive" empty="No overdue PSURs." />
      <Section title="Due within 30 days" items={within30} tone="warning" empty="Nothing due in the next 30 days." />
      <Section title="Due later" items={later} tone="success" empty="No future PSURs scheduled." />

      {!cal.isLoading && items.length === 0 && (
        <Card>
          <CardContent className="p-10 text-center text-sm text-muted-foreground">
            <CalendarDays className="mx-auto mb-2 h-6 w-6" /> No PSUR schedule available yet.
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function StatCard({
  label,
  value,
  tone,
}: {
  label: string;
  value: number;
  tone: 'success' | 'destructive' | 'warning';
}) {
  const cls =
    tone === 'destructive'
      ? 'text-destructive'
      : tone === 'warning'
        ? 'text-amber-600'
        : 'text-emerald-600';
  return (
    <Card>
      <CardContent className="p-4">
        <div className={`text-2xl font-bold ${cls}`}>{value}</div>
        <div className="text-xs text-muted-foreground">{label}</div>
      </CardContent>
    </Card>
  );
}

function Section({
  title,
  items,
  tone,
  empty,
}: {
  title: string;
  items: PsurCalendarItem[];
  tone: 'success' | 'destructive' | 'warning';
  empty: string;
}) {
  const bg =
    tone === 'destructive'
      ? 'bg-destructive/5 border-destructive/30'
      : tone === 'warning'
        ? 'bg-amber-500/5 border-amber-500/30'
        : 'bg-emerald-500/5 border-emerald-500/30';

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">{title}</CardTitle>
        <CardDescription>{items.length} report(s)</CardDescription>
      </CardHeader>
      <CardContent className="space-y-2">
        {items.length === 0 ? (
          <p className="text-sm text-muted-foreground">{empty}</p>
        ) : (
          items.map((i) => (
            <div
              key={i.id}
              className={`flex items-center justify-between rounded-md border p-3 ${bg}`}
            >
              <div>
                <div className="text-sm font-medium">{i.product ?? 'Unnamed product'}</div>
                <div className="text-xs text-muted-foreground">
                  {i.reportNumber ?? '-'} - {i.interval ?? '-'} - DLP{' '}
                  {i.dlp ? formatDate(i.dlp) : '-'}
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="text-right">
                  <div className="text-sm font-semibold">{formatDate(i.dueAt)}</div>
                  {i.status && <Badge variant="secondary">{i.status}</Badge>}
                </div>
                <Button asChild size="sm" variant="outline">
                  <Link to={`/psur/${i.id}`}>Open</Link>
                </Button>
              </div>
            </div>
          ))
        )}
      </CardContent>
    </Card>
  );
}
